/**
 * Discoverability Features
 *
 * Central feature registry for product discovery prompts.
 *
 * Primary responsibilities:
 * - Define trackable feature keys and prompt copy
 * - Keep CTA routes and trigger phrases in one place
 *
 * Dependencies:
 * - Canada Day Ultra Week campaign state
 */

import { isCanadaDayUltraWeekActive } from '@/lib/campaigns/canada-day-ultra-week'
import {
  YOUR_CREW_BETA_FEATURE_KEY,
  type BetaFeatureKey
} from '@/lib/beta-features/registry'

export const DISCOVERABILITY_FEATURE_KEYS = [
  'spaces',
  'files',
  'search',
  'planner',
  'images',
  'voice_memos',
  'connectors',
  'crew'
] as const

export type DiscoverabilityFeatureKey = (typeof DISCOVERABILITY_FEATURE_KEYS)[number]
export type DiscoverabilityCustomFeatureKey = `custom:${string}`
export type DiscoverabilityTrackedFeatureKey =
  | DiscoverabilityFeatureKey
  | DiscoverabilityCustomFeatureKey

export type DiscoverabilityPromptKind = 'have_you_tried' | 'new_feature'
export type DiscoverabilityPlanGate = 'free' | 'plus' | 'pro' | 'ultra' | 'business'

export const DISCOVERABILITY_PROMPT_KIND_LABELS: Record<DiscoverabilityPromptKind, string> = {
  have_you_tried: 'Nudge',
  new_feature: 'Launch'
}

export const DISCOVERABILITY_PLAN_GATE_LABELS: Record<DiscoverabilityPlanGate, string> = {
  free: 'Free',
  plus: 'Plus',
  pro: 'Pro',
  ultra: 'Ultra',
  business: 'Business'
}

export type DiscoverabilitySurface =
  | 'chat'
  | 'sidebar'
  | 'empty_state'
  | 'assistant_response'
  | 'spaces'
  | 'planner'
  | 'files'
  | 'search'
  | 'settings'
  | 'voice'
  | 'connectors'
  | 'crew'

export const DISCOVERABILITY_SURFACES = [
  'chat',
  'sidebar',
  'empty_state',
  'assistant_response',
  'spaces',
  'planner',
  'files',
  'search',
  'settings',
  'voice',
  'connectors',
  'crew'
] as const

export type DiscoverabilityFeature = {
  key: DiscoverabilityFeatureKey
  kind: DiscoverabilityPromptKind
  minimumPlan: DiscoverabilityPlanGate
  /**
   * When set, prompts/checklist/hints stay hidden unless this beta is enabled
   * for the user (fail closed while access is unknown).
   */
  requiresBetaFeature?: BetaFeatureKey
  label: string
  shortDescription: string
  tooltip: {
    title: string
    body: string
  }
  prompt: {
    title: string
    body: string
    ctaLabel: string
    ctaHref: string
  }
  surfaces: DiscoverabilitySurface[]
  intentPhrases: string[]
}

type DiscoverabilityFeatureCampaignCopy = {
  tooltip?: Partial<DiscoverabilityFeature['tooltip']>
  prompt?: Partial<DiscoverabilityFeature['prompt']>
}

export const DISCOVERABILITY_FEATURES: Record<DiscoverabilityFeatureKey, DiscoverabilityFeature> = {
  spaces: {
    key: 'spaces',
    kind: 'have_you_tried',
    minimumPlan: 'free',
    label: 'Spaces',
    shortDescription: 'Organize related chats, tasks, notes, files, and plans.',
    tooltip: {
      title: 'Spaces',
      body: 'Turn related work into an organized project with tasks, notes, and files.'
    },
    prompt: {
      title: 'Turn this into a Space',
      body: 'Keep related chats, tasks, notes, files, and plans together instead of losing them in chat.',
      ctaLabel: 'Create Space',
      ctaHref: '/spaces'
    },
    // Create Space dialog — real CTA, allowed under replies when intent matches.
    surfaces: ['chat', 'sidebar', 'assistant_response', 'empty_state', 'spaces'],
    intentPhrases: [
      'organize this',
      'create a space',
      'make a space',
      'turn this into a space'
    ]
  },
  files: {
    key: 'files',
    kind: 'have_you_tried',
    minimumPlan: 'free',
    label: 'Library',
    shortDescription: 'Let Losi read documents instead of pasting everything.',
    tooltip: {
      title: 'Library',
      body: 'Losi can read documents, spreadsheets, PDFs, and notes directly.'
    },
    prompt: {
      title: 'Work from a file in Library',
      body: 'Losi can review, summarize, rewrite, and analyze documents so you do not have to paste everything.',
      ctaLabel: 'Open Library',
      ctaHref: '/chat?sidebar=files'
    },
    // Sidebar-open only — never under assistant replies (enforced + registry).
    surfaces: ['chat', 'files', 'sidebar', 'empty_state'],
    intentPhrases: [
      'review this document',
      'summarize this pdf',
      'review my resume',
      'slide deck',
      'spreadsheet',
      'upload a file'
    ]
  },
  search: {
    key: 'search',
    kind: 'have_you_tried',
    minimumPlan: 'free',
    label: 'Search',
    shortDescription: 'Find old work across chats, Spaces, files, and memory.',
    tooltip: {
      title: 'Find anything in Losi',
      body: 'Search across chats, Spaces, files, memories, bookmarks, and starred messages.'
    },
    prompt: {
      title: 'Find anything in Losi',
      body: 'Search across chats, Spaces, files, memories, bookmarks, and starred messages without retracing your steps.',
      ctaLabel: 'Search Losi',
      ctaHref: '/chat'
    },
    surfaces: ['chat', 'search', 'sidebar', 'empty_state'],
    intentPhrases: [
      'where did we talk',
      'old chat',
      'previous conversation'
    ]
  },
  planner: {
    key: 'planner',
    kind: 'have_you_tried',
    minimumPlan: 'free',
    label: 'Planner',
    shortDescription: 'Turn tasks and schedules into an adjustable day plan.',
    tooltip: {
      title: 'Plan the day',
      body: 'Losi can place your work into a simple schedule you can adjust.'
    },
    prompt: {
      title: 'Turn this into a day plan',
      body: 'Losi can place your work into a simple schedule you can adjust instead of keeping it as a static list.',
      ctaLabel: 'Open Planner',
      ctaHref: '/spaces/planner'
    },
    surfaces: ['chat', 'planner', 'sidebar', 'assistant_response', 'empty_state'],
    intentPhrases: [
      'plan my day',
      'what should i do today',
      'time block'
    ]
  },
  images: {
    key: 'images',
    kind: 'have_you_tried',
    // Create Images is Plus+ on web/native — never suggest it on Free.
    minimumPlan: 'plus',
    label: 'Images',
    shortDescription: 'Generate and edit campaign visuals from a prompt.',
    tooltip: {
      title: 'Generate the visual here',
      body: 'Losi can create and edit campaign images directly from your prompt.'
    },
    prompt: {
      title: 'Generate the visual here',
      body: 'Losi can create and edit campaign images, thumbnails, posters, and brand concepts directly from your prompt.',
      ctaLabel: 'Create image',
      ctaHref: '/chat'
    },
    // Enables image tool in composer — real, intent-gated under replies.
    surfaces: ['chat', 'sidebar', 'assistant_response', 'empty_state'],
    intentPhrases: [
      'generate an image',
      'create a poster',
      'ad creative',
      'brand concept',
      'make a thumbnail'
    ]
  },
  voice_memos: {
    key: 'voice_memos',
    kind: 'have_you_tried',
    minimumPlan: 'free',
    label: 'Voice memos',
    shortDescription: 'Record spoken context when typing would slow you down.',
    tooltip: {
      title: 'Voice memos',
      body: 'Record a quick memo and let Losi transcribe the useful context.'
    },
    prompt: {
      title: 'Capture this as a voice memo',
      body: 'When a thought is easier to say than type, Losi can record it, transcribe it, and keep it with the conversation.',
      ctaLabel: 'Start voice memo',
      ctaHref: '/chat'
    },
    // Focuses mic — keep for empty/sidebar; not under every reply.
    surfaces: ['chat', 'voice', 'sidebar', 'empty_state'],
    intentPhrases: [
      'voice note',
      'voice memo',
      'audio note',
      'dictate this'
    ]
  },
  connectors: {
    key: 'connectors',
    kind: 'have_you_tried',
    minimumPlan: 'free',
    label: 'Integrations',
    shortDescription: 'Connect calendar, mail, and tools so Losi can act with your context.',
    tooltip: {
      title: 'Connect your tools',
      body: 'Link calendar, mail, and other apps so Losi can use real context instead of guesses.'
    },
    prompt: {
      title: 'Connect a tool Losi can use',
      body: 'Integrations let Losi read calendar, mail, and other apps you already use — the unlock for real work.',
      ctaLabel: 'Open Integrations',
      ctaHref: '/chat?sidebar=integrations'
    },
    // Sidebar-open only — never under assistant replies.
    surfaces: ['chat', 'settings', 'sidebar', 'empty_state', 'connectors'],
    intentPhrases: [
      'connect gmail',
      'connect slack',
      'connect outlook',
      'connect notion',
      'link my calendar'
    ]
  },
  crew: {
    key: 'crew',
    kind: 'have_you_tried',
    minimumPlan: 'free',
    requiresBetaFeature: YOUR_CREW_BETA_FEATURE_KEY,
    label: 'Crew',
    shortDescription: 'Spin up focused helpers for research, drafting, and parallel work.',
    tooltip: {
      title: 'Crew',
      body: 'Crew mates can research, draft, and run parallel work while you stay in chat.'
    },
    prompt: {
      title: 'Bring in a Crew mate',
      body: 'When the task needs parallel research or drafting, Crew can take a lane while you keep chatting.',
      ctaLabel: 'Open Crew',
      ctaHref: '/your-crew'
    },
    surfaces: ['chat', 'sidebar', 'assistant_response', 'empty_state', 'crew'],
    intentPhrases: [
      'bring in crew',
      'crew mate',
      'spin up helpers',
      'spin up crew'
    ]
  }
}

const CANADA_DAY_ULTRA_WEEK_FEATURE_COPY: Partial<
  Record<DiscoverabilityFeatureKey, DiscoverabilityFeatureCampaignCopy>
> = {
  spaces: {
    tooltip: {
      title: 'Spaces with Ultra unlocked',
      body: 'Turn this chat into an organized project with tasks, notes, files, and plans.'
    },
    prompt: {
      title: 'Spaces with Ultra unlocked',
      body: 'Use your free Ultra week to keep related chats, tasks, notes, files, and plans together.',
      ctaLabel: 'Create Space'
    }
  },
  files: {
    tooltip: {
      title: 'Open Library this week',
      body: 'Ultra is unlocked — drop a PDF, deck, resume, notes, or spreadsheet into Library.'
    },
    prompt: {
      title: 'Open Library this week',
      body: 'Use Ultra access to review, summarize, rewrite, or analyze a document without pasting everything.',
      ctaLabel: 'Open Library'
    }
  },
  images: {
    tooltip: {
      title: 'Create an image with Ultra',
      body: 'Generate visuals for campaigns, posts, ideas, and concepts directly in Losi.'
    },
    prompt: {
      title: 'Create an image with Ultra',
      body: 'Campaign images, thumbnails, posters, and visual concepts while Ultra is unlocked.',
      ctaLabel: 'Create image'
    }
  },
  planner: {
    tooltip: {
      title: 'Plan with Ultra unlocked',
      body: 'Turn your day or week into a practical schedule you can adjust.'
    },
    prompt: {
      title: 'Plan with Ultra unlocked',
      body: 'Use your free Ultra week to turn tasks and priorities into a simple schedule.',
      ctaLabel: 'Open Planner'
    }
  },
  search: {
    tooltip: {
      title: 'Find your old work',
      body: 'Search across chats, Spaces, files, memories, bookmarks, and starred messages.'
    },
    prompt: {
      title: 'Find your old work',
      body: 'Search across chats, Spaces, files, memories, bookmarks, and starred messages while Ultra is unlocked.',
      ctaLabel: 'Search Losi'
    }
  }
}

export const isDiscoverabilityFeatureKey = (value: unknown): value is DiscoverabilityFeatureKey => {
  return (
    typeof value === 'string' &&
    DISCOVERABILITY_FEATURE_KEYS.includes(value as DiscoverabilityFeatureKey)
  )
}

export const isDiscoverabilityCustomFeatureKey = (
  value: unknown
): value is DiscoverabilityCustomFeatureKey => {
  return typeof value === 'string' && /^custom:[a-z0-9][a-z0-9_-]{1,79}$/.test(value)
}

export const isDiscoverabilityTrackedFeatureKey = (
  value: unknown
): value is DiscoverabilityTrackedFeatureKey => {
  return isDiscoverabilityFeatureKey(value) || isDiscoverabilityCustomFeatureKey(value)
}

export const getDiscoverabilityFeature = (featureKey: DiscoverabilityFeatureKey) => {
  const feature = DISCOVERABILITY_FEATURES[featureKey]
  const campaignCopy = CANADA_DAY_ULTRA_WEEK_FEATURE_COPY[featureKey]

  if (!campaignCopy || !isCanadaDayUltraWeekActive()) {
    return feature
  }

  return {
    ...feature,
    tooltip: {
      ...feature.tooltip,
      ...campaignCopy.tooltip
    },
    prompt: {
      ...feature.prompt,
      ...campaignCopy.prompt
    }
  }
}

/** Beta key a discoverability prompt requires, if any (fail closed when disabled). */
export function getDiscoverabilityBetaRequirement(
  featureKey: DiscoverabilityFeatureKey
): BetaFeatureKey | null {
  return DISCOVERABILITY_FEATURES[featureKey].requiresBetaFeature ?? null
}

/**
 * Whether this discoverability feature is allowed to surface for the user.
 * Plan gates + beta gates. Unknown beta access → hidden (fail closed).
 */
export function isDiscoverabilityFeatureAvailable(
  featureKey: DiscoverabilityFeatureKey,
  input: {
    userPlan?: DiscoverabilityPlanGate | string | null
    enabledBetaFeatures?: Partial<Record<BetaFeatureKey, boolean>> | null
  } = {}
): boolean {
  const feature = DISCOVERABILITY_FEATURES[featureKey]
  const planRanks: Record<DiscoverabilityPlanGate, number> = {
    free: 0,
    plus: 1,
    pro: 2,
    ultra: 3,
    business: 4
  }
  const normalizePlan = (plan: typeof input.userPlan): DiscoverabilityPlanGate => {
    const value = String(plan || 'free').toLowerCase()
    if (value.startsWith('business') || value === 'enterprise') return 'business'
    if (value.startsWith('ultra') || value.startsWith('studio')) return 'ultra'
    if (value.startsWith('pro') || value.startsWith('byok')) return 'pro'
    if (value.startsWith('plus')) return 'plus'
    return 'free'
  }
  if (planRanks[normalizePlan(input.userPlan)] < planRanks[feature.minimumPlan]) {
    return false
  }
  const betaKey = feature.requiresBetaFeature
  if (betaKey && input.enabledBetaFeatures?.[betaKey] !== true) {
    return false
  }
  return true
}
